import { FC, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { PayrollPolicyPeriodTypesPeriodTypeEnum } from 'generated/resources/graphql'

import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { canAddExpensableToPayment } from 'modules/expenses/lib/expensables/availableActions'
import { expensableResource } from 'modules/expenses/lib/expensables/expensable'
import { ModalAction } from 'modules/expenses/lib/expensables/modalAction'

import AddToGroupModal from '../AddToGroupModal'
import AddToPaymentModal from '../AddToPaymentModal'
import AddToPayrollModal from '../AddToPayrollModal'
import RemoveFromGroupModal from '../RemoveFromGroupModal'

export type ActionFn = (expensables: Expensable[]) => void

interface ModalsProps {
  action: ModalAction | null
  expensables: Expensable[]
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  onReject: (reason?: string) => void
  onAddToPayroll: (period: PayrollPolicyPeriodTypesPeriodTypeEnum) => void
  onAddToPayment: (ids: number[], dueDate: Date) => void
  onAddToGroup: (groupId: number) => void
  onRemoveFromGroup: () => void
  onClose: () => void
}

interface UseActions {
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
}

interface UseActionsReturn {
  actions: {
    onApprove: ActionFn
    onRemoveFromPayroll: ActionFn
    onRestartReview: ActionFn
    onSetToPaid: ActionFn
    onRevertPaid: ActionFn
    onAddToGroup: ActionFn
    onAddToPayment: ActionFn
    onRemoveFromGroup: ActionFn
    onAddToPayroll: ActionFn
    onReject: ActionFn
  }
  Modals: FC
}

const idsFor = (expensables: Expensable[]) => expensables.map(({ id }) => id)
const withIds = (action: (ids: number[]) => void) => (expensables: Expensable[]) =>
  action(idsFor(expensables))

const Modals = ({
  action,
  expensables,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  onReject,
  onAddToPayroll,
  onAddToPayment,
  onAddToGroup,
  onRemoveFromGroup,
  onClose,
}: ModalsProps) => {
  const { canSyncWithBanking } = useOutletContext<RouteContextParams>()

  const selectedCategories = expensables
    .map((expensable) => expensableResource(expensable)?.category?.id)
    .filter((id): id is string => Boolean(id))

  return (
    <>
      {action === ModalAction.Reject && (
        <ReviewModal type="reject" onSubmit={onReject} onClose={onClose} />
      )}
      {action === ModalAction.CreatePayrollSupplement && (
        <AddToPayrollModal
          onSubmit={onAddToPayroll}
          onClose={onClose}
          currentPeriodOpen={currentPayrollPeriodOpen}
          availableCategories={availablePayrollCategories}
          selectedCategories={selectedCategories}
        />
      )}
      {action === ModalAction.AddToPayment && (
        <AddToPaymentModal
          expensables={expensables.filter((exp) =>
            canAddExpensableToPayment(exp, canSyncWithBanking)
          )}
          onSubmit={onAddToPayment}
          onClose={onClose}
        />
      )}
      {action === ModalAction.AddToGroup && (
        <AddToGroupModal onSubmit={onAddToGroup} onClose={onClose} />
      )}
      {action === ModalAction.RemoveFromGroup && (
        <RemoveFromGroupModal onSubmit={onRemoveFromGroup} onClose={onClose} />
      )}
    </>
  )
}

export const useActions = ({
  currentPayrollPeriodOpen,
  availablePayrollCategories,
}: UseActions): UseActionsReturn => {
  const [action, setAction] = useState<ModalAction | null>(null)
  const [expensables, setExpensables] = useState<Expensable[]>([])

  const ids = idsFor(expensables)

  const {
    onApprove,
    onReject,
    onAddToPayroll,
    onRemoveFromPayroll,
    onRestartReview,
    onSetToPaid,
    onRevertPaid,
    onAddToGroup,
    onAddToPayment,
    onRemoveFromGroup,
  } = useExpensableActions()

  const actions = {
    onApprove: withIds(onApprove),
    onRemoveFromPayroll: withIds(onRemoveFromPayroll),
    onRestartReview: withIds(onRestartReview),
    onSetToPaid: withIds(onSetToPaid),
    onRevertPaid: withIds(onRevertPaid),
    onAddToGroup: (_expensables: Expensable[]) => {
      setExpensables(_expensables)
      setAction(ModalAction.AddToGroup)
    },
    onRemoveFromGroup: (_expensables: Expensable[]) => {
      setExpensables(_expensables)
      setAction(ModalAction.RemoveFromGroup)
    },
    onAddToPayroll: (_expensables: Expensable[]) => {
      setExpensables(_expensables)
      setAction(ModalAction.CreatePayrollSupplement)
    },
    onAddToPayment: (_expensables: Expensable[]) => {
      setExpensables(_expensables)
      setAction(ModalAction.AddToPayment)
    },
    onReject: (_expensables: Expensable[]) => {
      setExpensables(_expensables)
      setAction(ModalAction.Reject)
    },
  }

  return {
    actions,
    Modals: () => (
      <Modals
        action={action}
        expensables={expensables}
        currentPayrollPeriodOpen={currentPayrollPeriodOpen}
        availablePayrollCategories={availablePayrollCategories}
        onReject={(reason) => onReject(ids, reason)}
        onAddToPayroll={(period) => onAddToPayroll(period, ids)}
        onAddToPayment={(expensableIds, dueDate) => onAddToPayment(expensableIds, dueDate)}
        onAddToGroup={(groupId) => onAddToGroup(ids, groupId)}
        onRemoveFromGroup={() => onRemoveFromGroup(ids)}
        onClose={() => {
          setAction(null)
          setExpensables([])
        }}
      />
    ),
  }
}
